import { faArrowRotateLeft, faFilter } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Skeleton, Stack } from '@mui/material'
import { getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import AbiStatementViewApiUrls from 'src/apiUrls/AbiStatementViewApiUrls'
import { useServerPagination } from 'src/views/abi/hooks/useServerPagination'
import { createDefaultValues } from 'src/components/era-package/forms/hooks/useFormConfig'
import RenderFormSection from 'src/components/era-package/forms/RenderFormSection'
import BlankCard from 'src/components/era-package/shared/BlankCard'
import { Table } from 'src/components/era-package/Table'
import { dateRangeFilter, globalFilterFn } from 'src/utils/era-package/filters'
import { StatementDialog } from 'src/views/abi/StatementPage/dialogs'
import { dailyStatementCols, dailyStatementFilters, optionsMap, STATEMENT_TYPE } from 'src/views/abi/StatementPage/shared'

const useDebounce = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

const DAILY_DEFAULT_PARAMS = {
  page: 0,
  size: 10,
  search: '',
  statementNumber: '',
  statementType: '',
  port: '',
  statementStatus: '',
  authorizationStatus: '',
  importerNumber: '',
  filerCode: '',
}

const DIALOG_PROPS_RESET = {
  open: false, data: {},
  type: STATEMENT_TYPE.daily
}

const DEFAULT_FILTERS = createDefaultValues(dailyStatementFilters)

const RESET_FILTERS = {
  ...DEFAULT_FILTERS,
  isFinalized: ''
};

// Exclude these fields from being auto-mapped onto the table column filter function.
const EXCLUDE_FILTERS = ['global', 'entryNumber']

export const DailyTab = () => {
  const filtersForm = useMemo(() => ({
    fields: dailyStatementFilters,
    title: 'Filters',
    icon: <FontAwesomeIcon icon={faFilter} />,
  }), [])
    const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [dialogProps, setDialogProps] = useState(DIALOG_PROPS_RESET)
  const debouncedFilters = useDebounce(filters, 500)

  const { data, totalElements, totalPages, loading, params, fetchPage, updateParams } =
    useServerPagination(AbiStatementViewApiUrls.LIST_DAILY, DAILY_DEFAULT_PARAMS)
  const isMounted = useRef(false)

  const pageIndex = params.page
  const pageSize = params.size

  const table = useReactTable({
    data, columns: dailyStatementCols,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: totalPages,
    getRowCanExpand: row => row.original.relatedEntrySummary?.length > 0,
    getSubRows: row => row.relatedEntrySummary,
    getRowId: row => `${row.statementNumber}-${row.isFinalized}`,
    globalFilterFn, filterFromLeafRows: true,
    filterFns: {
      dateRange: dateRangeFilter,
      relatedEntrySummary: (row, columnId, filterValue) => {
        const entries = row.getValue(columnId);
        if (!Array.isArray(entries)) return false;
        return entries.some(entry =>
          entry.toLowerCase().includes(filterValue.toLowerCase())
        );
      }
    },
    state: { pagination: { pageIndex, pageSize } },
    enableSorting: true,
    enableRowSelection: false,
    enableGlobalFilter: true,
  })

      // Filters: skip first mount run — hook auto-fetches with default params
  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    updateParams({
      search: debouncedFilters.global ?? '',
      statementNumber: debouncedFilters.statementNumber ?? '',
      statementType: debouncedFilters.statementType ?? '',
      port: debouncedFilters.port ?? '',
      statementStatus: debouncedFilters.statementStatus ?? '',
      authorizationStatus: debouncedFilters.authorizationStatus ?? '',
      importerNumber: debouncedFilters.importerNumber ?? '',
      filerCode: debouncedFilters.filerCode ?? '',
      page: 0,
    })
  }, [debouncedFilters]);
    
  const handleRowClick = (row) => {
    setDialogProps({
      open: true,
      statementNumber: [row.original.statementNumber, row.original.isFinalized],
      finalized: row.original.isFinalized,
      type: STATEMENT_TYPE.daily
    })
  }

  const handleNestedClick = (entryNumber, rowData) => {
    setDialogProps({
      open: true,
      statementNumber: [rowData.statementNumber, entryNumber],
      finalized: true,
      type: STATEMENT_TYPE.entry
    })
  }

  const handleRowExpansion = (row) => {
    const NestedRow = React.lazy(() => import('./NestedRow'));
    return (
      <React.Suspense fallback={<Skeleton />}>
        <NestedRow {...row} onEntrySelected={handleNestedClick} />
      </React.Suspense>
    );
  }

  const handleReset = () => {
    setFilters(RESET_FILTERS)
  }

  return <Stack spacing={2}>
    <BlankCard>
      <RenderFormSection section={filtersForm}
        formData={filters} setFormData={setFilters}
        optionsMap={optionsMap} dense spacing={1.5}
        actions={<Button onClick={handleReset}
          sx={{ minWidth: 120, px: 3, py: 1 }}
          startIcon={<FontAwesomeIcon icon={faArrowRotateLeft} />}
        >
          Reset Filters
        </Button>}
      />

    </BlankCard>
    <BlankCard>
      <Table table={table} hover
        rowCount={data.length}
        onRowClick={handleRowClick}
        renderExpandedRow={handleRowExpansion}
        onRefresh={() => fetchPage()}
        loading={loading}
      />
    </BlankCard>

    <StatementDialog
      onClose={() => setDialogProps(DIALOG_PROPS_RESET)}
      {...dialogProps}
    />
  </Stack>
}